import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Shield, CheckCircle2, AlertTriangle, ChevronRight, ChevronLeft, Upload, FileText, Clock, Award, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const COMPLIANCE_LEVELS = [
  { value: 'compliant', label: 'Compliant', color: 'bg-[#A8E6CF]/10 text-[#A8E6CF] border-[#A8E6CF]/30' },
  { value: 'substantially_compliant', label: 'Substantially Compliant', color: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  { value: 'not_compliant', label: 'Not Compliant', color: 'bg-red-500/10 text-red-400 border-red-500/30' },
  { value: 'not_assessed', label: 'Not Assessed', color: 'bg-muted text-muted-foreground border-border' },
];

export default function HIQAAssessment() {
  const { user } = useAuth();
  const [themes, setThemes] = useState([]);
  const [assessment, setAssessment] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/hiqa-assessment/current`, { withCredentials: true });
      setThemes(data.themes || []);
      if (data.assessment) {
        setAssessment(data.assessment);
        setCurrentTheme(data.assessment.current_theme || 0);
        const rMap = {};
        (data.assessment.responses || []).forEach(r => { rMap[r.standard_id] = r; });
        setResponses(rMap);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  const startAssessment = async () => {
    try {
      const { data } = await axios.post(`${API}/api/hiqa-assessment/start`, {}, { withCredentials: true });
      setAssessment(data);
      setCurrentTheme(0);
      setResponses({});
      toast.success('HIQA Self-Assessment started');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveResponse = async (standardId, field, value) => {
    const prev = responses[standardId] || {};
    const updated = { ...prev, [field]: value };
    setResponses(r => ({ ...r, [standardId]: updated }));

    if (!assessment?.id) return;
    try {
      await axios.put(`${API}/api/hiqa-assessment/respond`, {
        assessment_id: assessment.id,
        theme_id: themes[currentTheme]?.id,
        standard_id: standardId,
        compliance_level: updated.compliance_level || 'not_assessed',
        evidence: updated.evidence || '',
        action_required: updated.action_required || '',
        evidence_file_url: updated.evidence_file_url || null,
        current_theme: currentTheme,
      }, { withCredentials: true });
    } catch { /* silent save */ }
  };

  const uploadEvidence = async (standardId, file) => {
    if (!assessment?.id || !file) return;
    setUploading(standardId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('assessment_id', assessment.id);
      fd.append('standard_id', standardId);
      const { data } = await axios.post(`${API}/api/hiqa-assessment/evidence-upload`, fd, { withCredentials: true });
      saveResponse(standardId, 'evidence_file_url', data.url);
      setResponses(r => ({ ...r, [standardId]: { ...r[standardId], evidence_file_url: data.url, evidence_filename: data.filename } }));
      toast.success(`Evidence uploaded: ${data.filename}`);
    } catch { toast.error('Upload failed'); }
    setUploading(null);
  };

  const completeAssessment = async () => {
    if (!assessment?.id) return;
    if (!window.confirm('Complete the HIQA Self-Assessment? You can start a new one later.')) return;
    try {
      const { data } = await axios.post(`${API}/api/hiqa-assessment/complete`, { assessment_id: assessment.id }, { withCredentials: true });
      toast.success(`Assessment complete! Compliance rate: ${data.compliance_rate}%`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Loading HIQA assessment...</p></div></DashboardLayout>;

  const theme = themes[currentTheme];
  const totalStandards = themes.reduce((a, t) => a + t.standards.length, 0);
  const answeredCount = Object.values(responses).filter(r => r.compliance_level && r.compliance_level !== 'not_assessed').length;
  const compliantCount = Object.values(responses).filter(r => ['compliant', 'substantially_compliant'].includes(r.compliance_level)).length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="hiqa-assessment-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#00A6A0]" />HIQA Self-Assessment
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Guided workflow against the 8 National Standards themes</p>
          </div>
          {!assessment && (
            <Button className="btn-primary" onClick={startAssessment} data-testid="start-assessment-btn">Start Assessment</Button>
          )}
          {assessment?.status === 'in_progress' && (
            <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={completeAssessment} data-testid="complete-assessment-btn">
              <Award className="w-4 h-4 mr-1" />Complete Assessment
            </Button>
          )}
          {assessment?.status === 'completed' && (
            <Button className="btn-primary" onClick={async () => {
              try {
                const response = await axios.get(`${API}/api/hiqa-assessment/export/${assessment.id}`, { withCredentials: true, responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const a = document.createElement('a'); a.href = url;
                a.download = `HIQA_Self_Assessment_${new Date().toISOString().split('T')[0]}.pdf`;
                a.click(); window.URL.revokeObjectURL(url);
                toast.success('PDF downloaded');
              } catch { toast.error('Download failed'); }
            }} data-testid="header-download-pdf-btn">
              <Download className="w-4 h-4 mr-1" />Download PDF
            </Button>
          )}
        </div>

        {/* Progress */}
        {assessment && (
          <Card className="glass-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-foreground font-medium">Progress: {answeredCount}/{totalStandards} standards assessed</span>
                <span className={`text-sm font-bold ${assessment.status === 'completed' ? 'text-[#A8E6CF]' : 'text-[#00A6A0]'}`}>
                  {assessment.status === 'completed' ? `${assessment.compliance_rate || 0}% Compliant` : `${compliantCount} compliant so far`}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-[#00A6A0] rounded-full h-2 transition-all" style={{ width: `${totalStandards > 0 ? (answeredCount / totalStandards * 100) : 0}%` }} />
              </div>
              {/* Theme dots */}
              <div className="flex gap-1.5 mt-3">
                {themes.map((t, i) => {
                  const themeResponses = t.standards.filter(s => responses[s.id]?.compliance_level && responses[s.id].compliance_level !== 'not_assessed');
                  const allDone = themeResponses.length === t.standards.length;
                  return (
                    <button key={t.id} onClick={() => setCurrentTheme(i)} className={`w-8 h-8 rounded-lg text-xs font-bold flex items-center justify-center transition-all ${i === currentTheme ? 'bg-[#00A6A0] text-white ring-2 ring-[#00A6A0]/30' : allDone ? 'bg-[#A8E6CF]/20 text-[#A8E6CF]' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                      data-testid={`theme-dot-${i}`} title={t.name}>
                      {t.number}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No assessment yet */}
        {!assessment && (
          <div className="text-center py-16">
            <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
            <p className="text-lg text-foreground font-medium">No HIQA Self-Assessment started</p>
            <p className="text-sm text-muted-foreground mt-1">Click Start Assessment to begin a guided walkthrough of the 8 HIQA National Standards themes ({totalStandards} standards)</p>
          </div>
        )}

        {/* Theme Content */}
        {assessment && assessment.status !== 'completed' && theme && (
          <div className="space-y-4">
            {/* Theme Header */}
            <Card className="glass-card border-[#00A6A0]/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#00A6A0]/10 flex items-center justify-center text-lg font-bold text-[#00A6A0]">{theme.number}</div>
                  <div>
                    <p className="text-base font-medium text-foreground">{theme.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{theme.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Standards */}
            {theme.standards.map(std => {
              const resp = responses[std.id] || {};
              return (
                <Card key={std.id} className="glass-card border-border" data-testid={`standard-${std.id}`}>
                  <CardContent className="p-4">
                    <p className="text-sm text-foreground font-medium mb-3">
                      <span className="text-[#00A6A0] mr-1">{std.id}</span> {std.text}
                    </p>

                    {/* Compliance Level */}
                    <div className="flex gap-1.5 flex-wrap mb-3">
                      {COMPLIANCE_LEVELS.map(cl => (
                        <button key={cl.value} onClick={() => saveResponse(std.id, 'compliance_level', cl.value)}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${resp.compliance_level === cl.value ? cl.color + ' ring-1' : 'bg-muted/30 text-muted-foreground border-transparent hover:border-border'}`}
                          data-testid={`level-${std.id}-${cl.value}`}>
                          {cl.label}
                        </button>
                      ))}
                    </div>

                    {/* Evidence */}
                    <textarea value={resp.evidence || ''} onChange={e => saveResponse(std.id, 'evidence', e.target.value)}
                      placeholder="Evidence: Describe how your service meets this standard..."
                      className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[50px] mb-2" />

                    {/* Action Required */}
                    <textarea value={resp.action_required || ''} onChange={e => saveResponse(std.id, 'action_required', e.target.value)}
                      placeholder="Action required (if not compliant)..."
                      className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[36px] mb-2" />

                    {/* Evidence Upload */}
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="border-border text-xs" disabled={uploading === std.id}
                        onClick={() => { fileRef.current?.setAttribute('data-std', std.id); fileRef.current?.click(); }}
                        data-testid={`upload-evidence-${std.id}`}>
                        <Upload className="w-3 h-3 mr-1" />{uploading === std.id ? 'Uploading...' : 'Attach Evidence'}
                      </Button>
                      {(resp.evidence_file_url || resp.evidence_filename) && (
                        <span className="text-[10px] text-[#A8E6CF] flex items-center gap-1"><FileText className="w-3 h-3" />{resp.evidence_filename || 'File attached'}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Navigation */}
            <div className="flex justify-between pt-2">
              <Button variant="outline" className="border-border" disabled={currentTheme === 0} onClick={() => setCurrentTheme(t => t - 1)} data-testid="prev-theme-btn">
                <ChevronLeft className="w-4 h-4 mr-1" />Previous
              </Button>
              {currentTheme < themes.length - 1 ? (
                <Button className="btn-primary" onClick={() => setCurrentTheme(t => t + 1)} data-testid="next-theme-btn">
                  Next<ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={completeAssessment} data-testid="finish-assessment-btn">
                  <Award className="w-4 h-4 mr-1" />Complete Assessment
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Completed Assessment Summary */}
        {assessment?.status === 'completed' && (
          <Card className="glass-card border-[#A8E6CF]/30">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-[#A8E6CF] mx-auto mb-3" />
              <p className="text-lg font-medium text-foreground">Assessment Complete</p>
              <p className="text-3xl font-bold text-[#00A6A0] mt-2">{assessment.compliance_rate}%</p>
              <p className="text-sm text-muted-foreground mt-1">{assessment.compliant_count}/{assessment.total_standards} standards compliant</p>
              <p className="text-xs text-muted-foreground mt-2">Completed on {new Date(assessment.completed_at).toLocaleDateString()}</p>
              <div className="flex gap-3 justify-center mt-4">
                <Button className="btn-primary" onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/api/hiqa-assessment/export/${assessment.id}`, { withCredentials: true, responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const a = document.createElement('a'); a.href = url;
                    a.download = `HIQA_Self_Assessment_${new Date().toISOString().split('T')[0]}.pdf`;
                    a.click(); window.URL.revokeObjectURL(url);
                    toast.success('PDF downloaded');
                  } catch { toast.error('Download failed'); }
                }} data-testid="download-hiqa-pdf-btn">
                  <Download className="w-4 h-4 mr-1" />Download PDF Report
                </Button>
                <Button variant="outline" className="border-border" onClick={startAssessment} data-testid="new-assessment-btn">Start New Assessment</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={e => {
          const file = e.target.files?.[0];
          const stdId = fileRef.current?.getAttribute('data-std');
          if (file && stdId) uploadEvidence(stdId, file);
          e.target.value = '';
        }} />
      </div>
    </DashboardLayout>
  );
}
